import { Modal } from "@mantine/core";
import { Alert, Dialog, RadioGroup } from "evergreen-ui";
import _ from "lodash";
import { Fragment, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import Show from "../../../components/core/show";
import { FetchTreaties_fetchTreaties_data } from "../../../graphql/queries/__generated__/FetchTreaties";
import { getFlexibleName } from "../../../utils/helpers";
import TreatyDocumentViewer from "./TreatyDocumentViewer";
import { useTranslation } from "react-i18next";
import { BrokerTypes } from "graphql/__generated__/globalTypes";
import BrokerPermissionModal from "components/layout/broker-permission-modal";

type Props = {
    treaty: FetchTreaties_fetchTreaties_data | null;
};

const TreatyActionsMenu = ({ treaty }: Props) => {
    const { t } = useTranslation();
    const items = JSON.parse(treaty?.treaty?.layer_limit ?? "[]") as any[];
    const { broker } = useAppSelector((state) => state.offers);
    const [openDocViewer, setOpenDocViewer] = useState(false);
    const [openPayments, setOpenPayments] = useState(false);
    const [showQuarters, setShowQuarters] = useState(false);
    const [showLimits, setShowLimits] = useState(false);
    const [showBrokerPermission, setShowBrokerPermission] = useState(false);

    const isRestrictedBroker =
        broker === BrokerTypes.Kek || broker === BrokerTypes.Irisk;
    const [treaty_account_id, setTreaty_account_id] = useState(
        _.first(treaty?.treaty?.treaty_accounts)?.treaty_account_id ??
            _.first(items)?.uuid,
    );

    const isProp =
        treaty?.treaty?.treaty_program?.treaty_type === "PROPORTIONAL";
    const isNotEmptyAccounts = !_.isEmpty(treaty?.treaty?.treaty_accounts);
    const isNotEmptyLimits = !_.isEmpty(treaty?.treaty?.layer_limit);

    const layer = JSON.parse(treaty?.treaty?.layer_limit ?? "")?.find(
        (el: any) => el.uuid === treaty_account_id,
    );

    const layerNumber = JSON.parse(
        treaty?.treaty?.layer_limit ?? "",
    )?.findIndex((el: any) => el.uuid === treaty_account_id);

    const handleViewDocs = () => {
        if (isRestrictedBroker) {
            setShowBrokerPermission(true);
            return;
        }
        if (isProp) {
            if (isNotEmptyAccounts) {
                setShowQuarters(true);
            } else {
                setOpenDocViewer(true);
            }
        } else {
            if (isNotEmptyLimits) {
                setShowLimits(true);
            } else {
                setOpenDocViewer(true);
            }
        }
    };

    const handleQuartersConfirm = () => {
        setShowQuarters(false);
        setOpenDocViewer(true);
    };

    const handleLimitsConfirm = () => {
        setShowLimits(false);
        setOpenDocViewer(true);
    };

    return (
        <Fragment>
            <div className="flex space-x-3">
                <button
                    type="button"
                    onClick={handleViewDocs}
                    className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
                    <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    View Docs
                </button>

                <button
                    type="button"
                    onClick={() => setOpenPayments(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                    </svg>
                    View Payments
                </button>
            </div>

            <Dialog
                isShown={showQuarters}
                title={t("modal_titles.select_quarters")}
                onCloseComplete={() => setShowQuarters(false)}
                confirmLabel={t("common.buttons.continue")}
                onConfirm={
                    isNotEmptyAccounts
                        ? handleQuartersConfirm
                        : () => setShowQuarters(false)
                }>
                <Show if={isNotEmptyAccounts}>
                    <RadioGroup
                        label={t("treaty_details.quarters")}
                        size={16}
                        defaultChecked={false}
                        value={treaty_account_id}
                        options={
                            treaty?.treaty?.treaty_accounts?.map((item) => ({
                                label: getFlexibleName(
                                    item?.account_periods ?? "",
                                ),
                                value: item?.treaty_account_id ?? "",
                                checked:
                                    item?.treaty_account_id ===
                                    treaty_account_id,
                            })) ?? []
                        }
                        onChange={(event) =>
                            setTreaty_account_id(event.target.value)
                        }
                    />
                </Show>
                <Show if={!isNotEmptyAccounts}>
                    <Alert>
                        No Quarters have been filed for this Treaty by Broker
                    </Alert>
                </Show>
            </Dialog>

            <Dialog
                isShown={showLimits}
                title={t("modal_titles.select_layer")}
                onCloseComplete={() => setShowLimits(false)}
                confirmLabel={t("common.buttons.continue")}
                onConfirm={
                    isNotEmptyLimits
                        ? handleLimitsConfirm
                        : () => setShowLimits(false)
                }>
                <Show if={isNotEmptyLimits}>
                    <RadioGroup
                        label={t("treaty_details.layers")}
                        size={16}
                        value={treaty_account_id}
                        options={
                            JSON.parse(
                                treaty?.treaty?.layer_limit ?? "[]",
                            )?.map((item: any, index: number) => ({
                                label:
                                    t("treaty_details.layer") +
                                    " " +
                                    (index + 1),
                                value: item?.uuid ?? "",
                            })) ?? []
                        }
                        onChange={(event) =>
                            setTreaty_account_id(event.target.value)
                        }
                    />
                </Show>
                <Show if={!isNotEmptyLimits}>
                    <Alert>
                        No Quarters have been filed for this Treaty by Broker
                    </Alert>
                </Show>
            </Dialog>

            <BrokerPermissionModal
                open={showBrokerPermission}
                setOpen={setShowBrokerPermission}
                brokerName={broker ?? ""}
            />

            <TreatyDocumentViewer
                open={openDocViewer}
                setOpen={setOpenDocViewer}
                treaty={treaty}
                treatyAccountId={treaty_account_id}
                layer={layer}
                layerNumber={layerNumber}
            />

            <Modal
                opened={openPayments}
                onClose={() => setOpenPayments(false)}
                title={t("modal_titles.payments")}
                size={1200}>
                <div className="p-4">
                    <p className="text-gray-500">Payments...</p>
                </div>
            </Modal>
        </Fragment>
    );
};

export default TreatyActionsMenu;
